/**
 * ReflectionScreen — one question at a time.
 *
 * Sub-cards:
 *  0  lifePurpose         free text (no char counter — soft target)
 *  1  fifteenYearVision   free text
 *  2  weaknesses          free text (last)
 *
 * describeYourself is pre-filled from "About you" if blank (existing logic).
 * No character counter racing the user. Soft target label only.
 * All four fields feed into the profile — none are scored.
 */
import { useState } from "react";
import { Button } from "../components/Button";
import { QuestionCard } from "../components/QuestionCard";
import { TEXT_MIN } from "../flow/steps";
import { useFlow } from "../flow/FlowContext";
import { useT } from "../i18n";

const TOTAL = 3;

export function ReflectionScreen() {
  const { answers, profileAnswers, setProfileAnswers, next, canContinue } = useFlow();
  const t = useT();
  const f = profileAnswers.free;

  const patch = (partial: Partial<typeof f>) =>
    setProfileAnswers((p) => ({ ...p, free: { ...p.free, ...partial } }));

  // Pre-fill describeYourself from aboutYou if blank (existing behaviour).
  const ensureDescribe = () => {
    if (!f.describeYourself.trim() && answers.aboutYou.trim()) {
      patch({ describeYourself: answers.aboutYou.trim() });
    }
  };

  const [card, setCard] = useState(0);
  const pos = t("questionnaire.progressLabel", { current: String(card + 1), total: String(TOTAL) });
  const goBack = () => setCard((i) => Math.max(i - 1, 0));

  /* ── Card 0: Life purpose ── */
  if (card === 0) {
    return (
      <div className="screen-enter" style={{ flex: 1 }}>
        <QuestionCard
          question={t("reflection.lifePurpose")}
          subtext={t("questionnaire.fewSentences")}
          position={pos}
        >
          <label className="q-free">
            <textarea
              className="q-free__textarea"
              rows={5}
              maxLength={600}
              value={f.lifePurpose}
              onChange={(e) => patch({ lifePurpose: e.target.value })}
            />
          </label>
        </QuestionCard>
        <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
          <Button
            onClick={() => setCard(1)}
            disabled={f.lifePurpose.trim().length < TEXT_MIN.lifePurpose}
          >
            {t("common.continue")}
          </Button>
        </div>
      </div>
    );
  }

  /* ── Card 1: 15-year vision ── */
  if (card === 1) {
    return (
      <div className="screen-enter" style={{ flex: 1 }}>
        <QuestionCard
          question={t("reflection.fifteenYear")}
          subtext={t("questionnaire.fewSentences")}
          position={pos}
        >
          <label className="q-free">
            <textarea
              className="q-free__textarea"
              rows={5}
              maxLength={600}
              value={f.fifteenYearVision}
              onChange={(e) => patch({ fifteenYearVision: e.target.value })}
            />
          </label>
          <button type="button" className="q-subnav__back" style={{ marginTop: 12 }} onClick={goBack}>
            ← {t("shell.back")}
          </button>
        </QuestionCard>
        <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
          <Button
            onClick={() => setCard(2)}
            disabled={f.fifteenYearVision.trim().length < TEXT_MIN.fifteenYearVision}
          >
            {t("common.continue")}
          </Button>
        </div>
      </div>
    );
  }

  /* ── Card 2 (last): Weaknesses ── */
  const onContinue = () => {
    ensureDescribe();
    next();
  };

  return (
    <div className="screen-enter" style={{ flex: 1 }}>
      <QuestionCard
        question={t("reflection.weaknesses")}
        subtext={t("questionnaire.fewSentences")}
        position={pos}
      >
        <label className="q-free">
          <textarea
            className="q-free__textarea"
            rows={4}
            maxLength={400}
            value={f.weaknesses}
            onChange={(e) => patch({ weaknesses: e.target.value })}
          />
        </label>
        <button type="button" className="q-subnav__back" style={{ marginTop: 12 }} onClick={goBack}>
          ← {t("shell.back")}
        </button>
      </QuestionCard>
      <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Button onClick={onContinue} disabled={!canContinue}>
          {t("common.continue")}
        </Button>
      </div>
    </div>
  );
}
